import PropTypes from 'prop-types';
import { PUBLIC_BASE } from 'app/config';
import { setIsLoadingNextComponent } from 'app/shared/modules/pageTemplate/actions';

import { logger } from './logger';

export const isCrawler = () => {
  const ua = window.navigator.userAgent.toLowerCase();

  return ua?.includes('googlebot');
};

export const createGUID = () => {
  const s4 = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  return `${s4()}${s4()}${s4()}${s4()}${s4()}${s4()}${s4()}${s4()}`;
};

export const monthsDiff = (d1, d2) => {
  let months = (d2.getFullYear() - d1.getFullYear()) * 12;

  months -= d1.getMonth();
  months += d2.getMonth();
  return months <= 0 ? 0 : months;
};

export const isIE11 = () =>
  !!(
    navigator.userAgent.match(/Trident/) &&
    navigator.userAgent.match(/rv[ :]11/)
  );

export const isMsBrowser = () => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  const trident = ua.indexOf('Trident/');
  const edge = ua.indexOf('Edge/');

  return [msie, trident, edge].some(browser => browser > 0);
};

export const recursiveParse = json => {
  let parsed;

  try {
    parsed = JSON.parse(json);
  } catch (error) {
    return json;
  }

  if (!parsed) {
    return parsed;
  }
  Object.keys(parsed).forEach(key => {
    if (typeof parsed[key] === 'string') {
      try {
        parsed[key] = recursiveParse(parsed[key]);
      } catch (error) {
        // ignore parsing error, assume variable is supposed to be a string
      }
    }
  });
  return parsed;
};

export const getCookiesContainingKey = (
  cookieString,
  cookieKey,
  allowPrefix,
) => {
  return cookieString
    .split(';')
    .map(cookiePart => {
      const parts = cookiePart.trim().split('=');

      return {
        [parts[0]]: parts[1],
      };
    })
    .filter(cookie => {
      const key = Object.keys(cookie)[0];

      if (allowPrefix) {
        return key.indexOf(cookieKey) > -1;
      }
      return key === cookieKey;
    });
};

/* istanbul ignore next */
export const getCookiesNotContainingKey = (cookieString, allowedKey) => {
  return cookieString
    .split(';')
    .map(cookiePart => {
      const parts = cookiePart.trim().split('=');

      return {
        [parts[0]]: parts[1],
      };
    })
    .filter(cookie => {
      const key = Object.keys(cookie)[0].toLowerCase();

      return key.indexOf(allowedKey.toLowerCase()) === -1;
    });
};

/**
 * Localstorage Support Check
 *
 * @returns {boolean} Local Storage Support
 */
export function isLocalStorageSupported() {
  /* istanbul ignore next */
  try {
    const storage = window.localStorage;
    const key = 'localStorageSupport';

    storage.setItem(key, 'true');
    storage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Cookie Support
 *
 * @export
 * @param {*} [cookieStorage={}] Cookie Storage from 'redux-persist-cookie-storage'
 * @returns {boolean} Cookie Support
 */
export function isCookieSupported(cookieStorage = {}) {
  // It's needed by Redux Cookie Plugin
  if (cookieStorage.cookies) {
    return true;
  }

  /* istanbul ignore next */
  try {
    const key = 'cookiestoragesupport';
    // Create cookie

    document.cookie = `${key}=1`;
    const ret = document.cookie.indexOf(`${key}=`) !== -1;
    // Delete cookie

    document.cookie = `${key}=1; expires=Thu, 01-Jan-1970 00:00:01 GMT`;
    return ret;
  } catch (error) {
    return false;
  }
}

export const ChildrenShape = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node,
]);

export const getEnvironmentValue = (ENV_NAME, fallBack) => {
  let envValue = fallBack;
  if (
    typeof process !== 'undefined' &&
    !process.browser &&
    process.env[ENV_NAME] !== undefined
  ) {
    envValue = process.env[ENV_NAME];
  }

  if (typeof window !== 'undefined' && window[ENV_NAME]) {
    envValue = window[ENV_NAME];
  }

  return envValue;
};

/**
 * @description returns an Effect which instructs the middleware
 * to call a given function with the given arguments.
 *
 *
 * ( useful to replace the 'delay' function from
 * 'redux-saga' when needed to test the sagas files )
 */
export const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * A function that returns a function that loads a component
 * @param exportName - the name of the exported component inside of the file you are importing
 * @param componentImportFunction - the function to actually import the file, this can not be abstracted here because of the way webpack does dynamic imports
 * @return {Function} - loads a route and handles the changes on redux/pagetemplate
 */
export const getAsyncRouteComponentLoader = (
  exportName,
  componentImportFunction,
) => {
  if (typeof exportName !== 'string') {
    logger.error(
      `ExportName can't be ${typeof exportName}.`,
      'You have to pass in the export name of the component you are importing',
    );
    throw new TypeError(`ExportName can't be ${typeof exportName}.`);
  }

  if (typeof componentImportFunction !== 'function') {
    logger.error(
      `importFn can't be ${typeof exportName}.`,
      'you have to pass in a function that loads the component',
    );
    throw new TypeError(`importFn can't be ${typeof exportName}.`);
  }

  const routeNames = { [exportName]: false };

  return (loc, cb) => {
    const hasWindowObject = typeof window !== 'undefined';
    const routeWasLoaded = routeNames[exportName];

    if (
      hasWindowObject && // we need to be on the client
      !routeWasLoaded && // and the route cannot be loaded from the client already
      !window._WAS_RENDERED_ON_SERVER_ // and we cannot come from a server side render
    ) {
      window.store.dispatch(setIsLoadingNextComponent(true));
    }

    return componentImportFunction().then(c => {
      if (
        hasWindowObject && // we need to be on the client
        !routeWasLoaded // and the route cannot be loaded from the client already
      ) {
        window.store.dispatch(setIsLoadingNextComponent(false));
      }
      routeNames[exportName] = true;
      cb(null, c[exportName]);
    });
  };
};

/**
 * temp helper for svg icons
 * @TODO build an SVG component
 */

export const SVG_ICONS = {
  ATTENTION: 'attention',
  INFO: 'info',
  GO_PROFILE_PAGE: 'go_profile_page',
  GO_PROFILE_PAGE_HOVER: 'go_profile_page_hover',
};

/**
 * @param {'attention'|'info'} iconName
 * @returns {string}
 */
export const getIcon = iconName => {
  switch (iconName) {
    case SVG_ICONS.ATTENTION:
      return `${PUBLIC_BASE}/assets/dealer/icon-attention.svg`;
    case SVG_ICONS.INFO:
      return `${PUBLIC_BASE}/assets/dealer/icon-info.svg`;
    case SVG_ICONS.GO_PROFILE_PAGE:
      return `${PUBLIC_BASE}/assets/dealer/icon-go-2-profile-page.svg`;
    case SVG_ICONS.GO_PROFILE_PAGE_HOVER:
      return `${PUBLIC_BASE}/assets/dealer/icon-go-2-profile-page-hover.svg`;
    default:
      return '';
  }
};
/**
 * Function used to fill out default props
 */
export const noop = () => {};
